/**
 * The "updates" dot on My Library: a moderator has approved or removed one of your designs since you
 * last looked at your published designs. Client only.
 *
 * The server says WHEN a moderator last decided on one of the viewer's designs (never which one or
 * what); the browser remembers the last decision the viewer has SEEN, per user, in localStorage. Per
 * device on purpose — nothing new to store server-side, and the worst case is the dot showing once
 * more on a second device. The server answer — including a FAILED answer — is cached for 10 minutes per
 * tab, so the sidebar, which renders on every page, can never turn into a request per navigation.
 */

export const PUBLISHED_SEEN_EVENT = 'mcwt-published-seen';
const CACHE_KEY = 'mcwt_published_status';
const CACHE_MS = 10 * 60_000;
const seenKey = (userId: string) => `mcwt_published_seen_at:${userId}`;

function read(storage: 'local' | 'session', key: string): string | null {
  try {
    return (storage === 'local' ? window.localStorage : window.sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function write(storage: 'local' | 'session', key: string, value: string): void {
  try {
    (storage === 'local' ? window.localStorage : window.sessionStorage).setItem(key, value);
  } catch { /* storage blocked: the dot simply never shows */ }
}

const cacheAnswer = (userId: string, latest: string | null) =>
  write('session', CACHE_KEY, JSON.stringify({ userId, at: Date.now(), latest }));

/** When a moderator last decided on one of this user's designs, or null (also when the check failed). */
export async function fetchLatestDecision(userId: string): Promise<string | null> {
  const cached = read('session', CACHE_KEY);
  if (cached) {
    try {
      const c = JSON.parse(cached) as { userId: string; at: number; latest: string | null };
      if (c.userId === userId && Date.now() - c.at < CACHE_MS) return c.latest;
    } catch { /* ignore a malformed cache */ }
  }
  let latest: string | null = null;
  try {
    const res = await fetch('/api/designs/status', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    if (res.ok) {
      const data = (await res.json().catch(() => ({}))) as { latestReviewedAt?: string | null };
      latest = typeof data.latestReviewedAt === 'string' ? data.latestReviewedAt : null;
    }
  } catch { /* offline or blocked: treated as "no update", and cached like one */ }
  // Cached whether or not it worked. An uncached failure was re-requested on EVERY navigation, which
  // could burn the shared community rate limit just by browsing.
  cacheAnswer(userId, latest);
  return latest;
}

export function hasUnseenDecision(userId: string, latest: string | null): boolean {
  if (!latest) return false;
  const seen = read('local', seenKey(userId));
  return !seen || Date.parse(latest) > Date.parse(seen);
}

/** Record that the user has now seen every decision on these designs, and tell the sidebar. */
export function markPublishedSeen(userId: string, designs: readonly { reviewedAt: string | null }[]): void {
  let latest: string | null = null;
  for (const d of designs) {
    if (d.reviewedAt && (!latest || Date.parse(d.reviewedAt) > Date.parse(latest))) latest = d.reviewedAt;
  }
  // Only ever move FORWARD. Unpublishing the newest-reviewed design used to move "seen" back to an older
  // decision, so the sidebar's cached newer decision made the dot reappear and never clear.
  const seen = read('local', seenKey(userId));
  if (latest && (!seen || Date.parse(latest) > Date.parse(seen))) write('local', seenKey(userId), latest);
  try {
    window.dispatchEvent(new Event(PUBLISHED_SEEN_EVENT));
  } catch { /* no window */ }
}
